import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import Dashboard from '../views/Dashboard.vue'
import BlogPost from '../views/BlogPost.vue'
import Login from '../views/Login.vue'
import Signup from '../views/Signup.vue'
import Search from '../views/Search.vue'
import LikedPosts from '../views/LikedPosts.vue'
import BookmarkedPosts from '../views/BookmarkedPosts.vue'
import store from '../store'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      title: 'Skix Inc Tech Blog - Home',
      metaTags: [
        {
          name: 'description',
          content: 'Explore the latest articles on software development, AI, and technology from Skix Inc.'
        },
        {
          property: 'og:title',
          content: 'Skix Inc Tech Blog - Home'
        },
        {
          property: 'og:description',
          content: 'Explore the latest articles on software development, AI, and technology from Skix Inc.'
        }
      ]
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { 
      requiresAuth: true,
      title: 'Your Dashboard - Skix Inc Tech Blog'
    }
  },
  {
    path: '/post/:id',
    name: 'BlogPost',
    component: BlogPost,
    meta: {
      title: 'Blog Post - Skix Inc Tech Blog',
      metaTags: [
        {
          name: 'description',
          content: 'Read this insightful article on Skix Inc Tech Blog'
        },
        {
          property: 'og:title',
          content: 'Blog Post - Skix Inc Tech Blog'
        },
        {
          property: 'og:description',
          content: 'Read this insightful article on Skix Inc Tech Blog'
        }
      ]
    }
  },
  {
    path: '/post/new',
    name: 'NewPost',
    component: () => import('../views/EditPost.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Create New Post - Skix Inc Tech Blog'
    }
  },
  {
    // Changed path from /post/:id/edit to /draft/:id/edit
    path: '/draft/:id/edit', 
    name: 'EditPost',
    component: () => import('../views/EditPost.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Edit Post - Skix Inc Tech Blog'
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      title: 'Login - Skix Inc Tech Blog'
    }
  },
  {
    path: '/signup',
    name: 'Signup',
    component: Signup,
    meta: {
      title: 'Sign Up - Skix Inc Tech Blog'
    }
  },
  {
    path: '/search',
    name: 'Search',
    component: Search,
    meta: {
      title: 'Search Results - Skix Inc Tech Blog',
      metaTags: [
        {
          name: 'description',
          content: 'Search for articles on software development, AI, and technology on Skix Inc Tech Blog'
        },
        {
          property: 'og:title',
          content: 'Search Results - Skix Inc Tech Blog'
        },
        {
          property: 'og:description',
          content: 'Search for articles on software development, AI, and technology on Skix Inc Tech Blog'
        }
      ]
    }
  },
  {
    path: '/liked',
    name: 'LikedPosts',
    component: LikedPosts,
    meta: {
      requiresAuth: true,
      title: 'Posts You\'ve Liked - Skix Inc Tech Blog'
    }
  },
  {
    path: '/bookmarks',
    name: 'BookmarkedPosts',
    component: BookmarkedPosts,
    meta: {
      requiresAuth: true,
      title: 'Your Bookmarked Posts - Skix Inc Tech Blog'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach((to, from, next) => {
  // This goes through the matched routes from last to first, finding the closest route with a title.
  // e.g., if we have `/some/deep/nested/route` and `/some`, `/deep`, and `/nested` have titles,
  // `/nested`'s will be chosen.
  const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);

  // Find the nearest route element with meta tags.
  const nearestWithMeta = to.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);

  // If a route with a title was found, set the document (page) title to that value.
  if(nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  }

  // Remove any stale meta tags from the document using the key attribute we set below.
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(el => el.parentNode.removeChild(el));

  // Skip rendering meta tags if there are none.
  if(!nearestWithMeta) {
    // Check for authentication requirement
    if (to.matched.some(record => record.meta.requiresAuth)) {
      if (!store.getters.isAuthenticated) {
        next({
          path: '/login',
          query: { redirect: to.fullPath }
        });
      } else {
        next();
      }
    } else {
      next();
    }
    return;
  }

  // Turn the meta tag definitions into actual elements in the head.
  nearestWithMeta.meta.metaTags.forEach(tagDef => {
    const tag = document.createElement('meta');

    Object.keys(tagDef).forEach(key => {
      tag.setAttribute(key, tagDef[key]);
    });

    // We use this to track which meta tags we create so we don't interfere with other ones.
    tag.setAttribute('data-vue-router-controlled', '');

    document.head.appendChild(tag);
  });

  // Check for authentication requirement
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.isAuthenticated) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      });
    } else {
      next();
    }
  } else {
    next();
  }
})

export default router
